export interface OrderItem {
  recipeId: string;
  recipeTitle: string;
  recipeTitleArabic?: string;
  recipeImage?: string;
  quantity: number;
  unitPriceEGP: number;
  lineTotalEGP: number;
}

export interface Order {
  _id: string;
  /** Customer-facing reference: 6-digit code (e.g. 048291). Legacy orders may use other shapes. */
  orderNumber?: string;
  /** Internal monotonic sequence (admin only; not shown in customer app). */
  internalOrderSequence?: number;
  userId: {
    _id: string;
    name: string;
    email: string;
  };
  recipeId?: string;
  recipeTitle?: string;
  recipeTitleArabic?: string;
  recipeImage?: string;
  quantity?: number;
  items?: OrderItem[];
  subtotalEGP?: number;
  totalPriceEGP: number;
  deliveryAddress: {
    label: string;
    street: string;
    city: string;
    country: string;
    building?: string;
    floor?: string;
    apartment?: string;
    phoneNumber?: string;
  };
  status: 'pending' | 'pending_payment' | 'confirmed' | 'preparing' | 'ready' | 'out_for_delivery' | 'delivered' | 'cancelled' | 'rejected' | 'issue';
  paymentMethod: 'cod' | 'instapay' | 'wallet';
  paymentStatus?: 'pending_upload' | 'awaiting_admin_review' | 'approved' | 'rejected';
  notes?: string;
  estimatedDeliveryTime?: Date | string;
  deliveredAt?: Date | string;
  rejectionReason?: string;
  refundedAmountEGP?: number;
  createdAt: string;
  updatedAt: string;
}

export const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  pending_payment: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  preparing: 'bg-purple-100 text-purple-800',
  ready: 'bg-indigo-100 text-indigo-800',
  out_for_delivery: 'bg-cyan-100 text-cyan-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-800',
  rejected: 'bg-red-100 text-red-800',
  issue: 'bg-amber-100 text-amber-800',
};

export const ORDER_STATUS_KEYS: Record<string, string> = {
  pending: 'statusPending',
  pending_payment: 'statusPendingPayment',
  confirmed: 'statusConfirmed',
  preparing: 'statusPreparing',
  ready: 'statusReady',
  out_for_delivery: 'statusOutForDelivery',
  delivered: 'statusDelivered',
  cancelled: 'statusCancelled',
  rejected: 'statusRejected',
  issue: 'statusIssue',
};

export const ALL_STATUSES = ['pending_payment', 'pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery', 'delivered', 'cancelled', 'rejected'] as const;

export const HISTORY_EVENT_KEYS: Record<string, string> = {
  created: 'orders.historyEventCreated',
  status_change: 'orders.historyEventStatusChange',
  payment_approved: 'orders.historyEventPaymentApproved',
  payment_rejected: 'orders.historyEventPaymentRejected',
  eta_updated: 'orders.historyEventEtaUpdated',
};

export function formatHistoryEvent(
  t: (key: string) => string,
  event: { eventType: string; fromValue?: string; toValue?: string; note?: string },
): string {
  const label = HISTORY_EVENT_KEYS[event.eventType] ? t(HISTORY_EVENT_KEYS[event.eventType]) : event.eventType;
  if (event.eventType === 'status_change' && event.fromValue != null && event.toValue != null) {
    return `${label}: ${event.fromValue} → ${event.toValue}`;
  }
  if (event.eventType === 'payment_rejected' && event.note) {
    return `${label}: ${event.note}`;
  }
  if (event.eventType === 'eta_updated' && event.toValue) {
    try {
      const d = new Date(event.toValue);
      return `${label}: ${d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}`;
    } catch {
      return label;
    }
  }
  return label;
}
